/* ==========================================================================
   The Plank by Modgav — behaviour
   Shopify port. Every initialiser is scoped to a root element and is safe to
   run more than once, because the theme editor re-renders a section on every
   settings change.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var noMotion = reduced || saveData;

  /* One observer per concern, reused across re-inits so a section that is
     edited repeatedly does not leak observers. */
  var revealIO = null;
  var videoIO = null;
  var watch = new Map();
  var seenVideos = new WeakSet();

  /* ---------- year ---------- */
  function initYear(root) {
    var yr = root.querySelector('#yr');
    if (yr) yr.textContent = new Date().getFullYear();
  }

  /* ======================================================================
     REVEALS
     ====================================================================== */
  function buildRevealIO() {
    if (revealIO) return revealIO;
    revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        var list = watch.get(e.target) || [];
        list.forEach(function (el) {
          if (el.hasAttribute('data-stagger')) {
            Array.prototype.forEach.call(el.children, function (child, i) {
              child.style.transitionDelay = (i * 65) + 'ms';
            });
          }
          el.classList.add('is-in');
        });
        revealIO.unobserve(e.target);
        watch.delete(e.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    return revealIO;
  }

  function initReveals(root) {
    var targets = root.querySelectorAll('[data-r], [data-mask], [data-wipe], [data-stagger]');
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(targets, function (t) { t.classList.add('is-in'); });
      return;
    }

    /* [data-mask] and [data-wipe] start life clipped to zero area, and a
       zero-area box never reaches an IntersectionObserver threshold. So watch
       an unclipped ancestor and reveal the clipped element from there. */
    var io = buildRevealIO();
    var hosts = [];
    Array.prototype.forEach.call(targets, function (el) {
      if (el.classList.contains('is-in')) return;          // already revealed
      var clipped = el.hasAttribute('data-mask') || el.hasAttribute('data-wipe');
      var host = clipped ? (el.parentElement || el) : el;
      if (!watch.has(host)) { watch.set(host, []); hosts.push(host); }
      if (watch.get(host).indexOf(el) === -1) watch.get(host).push(el);
    });
    hosts.forEach(function (host) { io.observe(host); });
  }

  /* Sibling reveals inside a block cascade rather than firing together. */
  function stampDelays(root) {
    var blocks = root.querySelectorAll('.split__text, .band__body, .hero__body, .wrap, .ledger, .signup__card');
    Array.prototype.forEach.call(blocks, function (b) {
      var kids = b.querySelectorAll(':scope > [data-r], :scope > [data-mask]');
      Array.prototype.forEach.call(kids, function (k, i) {
        if (!k.style.transitionDelay) k.style.transitionDelay = (i * 85) + 'ms';
      });
    });
  }

  /* ======================================================================
     VIDEO — load and play only while on screen

     Shopify's video_tag emits its own <video> markup, so this matches the
     element itself rather than any wrapper class. Videos flagged data-manual
     carry audio and are click-to-play, never auto.
     ====================================================================== */
  function buildVideoIO() {
    if (videoIO) return videoIO;
    videoIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var v = e.target;
        if (e.isIntersecting) {
          if (!v.dataset.loaded) {
            v.dataset.loaded = '1';
            v.load();
          }
          var p = v.play();
          if (p && p.catch) p.catch(function () { /* autoplay refused, poster stands */ });
        } else if (!v.paused) {
          v.pause();
        }
      });
    }, { rootMargin: '220px 0px', threshold: 0.1 });
    return videoIO;
  }

  function initVideo(root) {
    /* data-manual opts a video out of autoplay, either on the element or on
       a wrapper — a film with sound sits inside a [data-manual] figure, and
       starting it here would be both unwanted and blocked by the browser. */
    var vids = Array.prototype.slice
      .call(root.querySelectorAll('video:not([data-manual])'))
      .filter(function (v) { return !v.closest('[data-manual]'); });
    if (!vids.length) return;

    /* reduced motion or data saver: posters only, nothing loads */
    if (noMotion || !('IntersectionObserver' in window)) return;

    var io = buildVideoIO();
    vids.forEach(function (v) {
      if (seenVideos.has(v)) return;
      seenVideos.add(v);
      io.observe(v);
    });
  }

  /* ======================================================================
     MARQUEE — duplicate the list so the loop is seamless

     Guarded: a second run would otherwise quadruple the items.
     ====================================================================== */
  function initMarquee(root) {
    var tracks = root.querySelectorAll('.marquee__track');
    Array.prototype.forEach.call(tracks, function (track) {
      if (track.dataset.marqueeReady === '1') return;
      track.dataset.marqueeReady = '1';
      var html = track.innerHTML;
      track.innerHTML = html + html;
      Array.prototype.forEach.call(track.children, function (li, i) {
        if (i >= track.children.length / 2) li.setAttribute('aria-hidden', 'true');
      });
    });
  }

  /* ======================================================================
     SPEC PINSTAGE — phone behaviour

     Above 860px the diagram stands on its own and this does nothing. Below
     it the stage is sticky and the annotations are a list, so the active dot
     and the active row have to track each other both ways: scrolling the
     list moves the dot, tapping a dot scrolls the list.
     ====================================================================== */
  var PIN_MQ = '(max-width: 860px)';

  function initPinstage(root) {
    var stages = root.querySelectorAll('[data-pinstage]');
    Array.prototype.forEach.call(stages, function (wrap) {
      if (wrap.dataset.pinstageReady === '1') return;
      wrap.dataset.pinstageReady = '1';

      var stage = wrap.querySelector('.pinstage');
      var dots = Array.prototype.slice.call(wrap.querySelectorAll('[data-pin-dot]'));
      var rows = Array.prototype.slice.call(wrap.querySelectorAll('[data-pin-item]'));
      var capNum = wrap.querySelector('[data-pin-cap-num]');
      var capTitle = wrap.querySelector('[data-pin-cap-title]');
      if (!stage || !rows.length) return;

      var mq = window.matchMedia(PIN_MQ);
      var active = -1;
      var lockUntil = 0;

      function setActive(i) {
        if (i === active || i < 0 || i >= rows.length) return;
        active = i;
        dots.forEach(function (d, n) { d.classList.toggle('is-on', n === i); });
        rows.forEach(function (r, n) { r.classList.toggle('is-on', n === i); });

        var title = rows[i].querySelector('.pinlist__title');
        var label = ('0' + (i + 1)).slice(-2);
        if (capNum) capNum.textContent = label;
        if (capTitle && title) capTitle.textContent = title.textContent;
      }

      /* the row sitting closest beneath the sticky stage is the one being read */
      function spy() {
        if (!mq.matches || Date.now() < lockUntil) return;
        var edge = stage.getBoundingClientRect().bottom;
        var best = -1;
        var bestDist = Infinity;
        rows.forEach(function (r, n) {
          var box = r.getBoundingClientRect();
          if (box.bottom <= edge) return;
          var dist = Math.abs(box.top - (edge + 40));
          if (dist < bestDist) { bestDist = dist; best = n; }
        });
        if (best >= 0) setActive(best);
      }

      var ticking = false;
      function onScrollSpy() {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () { spy(); ticking = false; });
      }

      function goTo(i) {
        setActive(i);
        if (!mq.matches) return;
        lockUntil = Date.now() + 700;
        /* measured from the stage's live bottom edge, so the topbar offset is
           already accounted for wherever the stage has settled */
        var edge = stage.getBoundingClientRect().bottom;
        var y = window.pageYOffset + rows[i].getBoundingClientRect().top - edge - 8;
        window.scrollTo({ top: y, behavior: reduced ? 'auto' : 'smooth' });
      }

      dots.forEach(function (d) {
        d.addEventListener('click', function () { goTo(parseInt(d.dataset.pinDot, 10)); });
      });
      rows.forEach(function (r) {
        r.addEventListener('click', function () { goTo(parseInt(r.dataset.pinItem, 10)); });
      });

      window.addEventListener('scroll', onScrollSpy, { passive: true });
      window.addEventListener('resize', onScrollSpy, { passive: true });

      setActive(0);
      spy();
    });
  }

  /* ======================================================================
     TOPBAR
     ====================================================================== */
  /* The topbar is fixed and changes height when it sticks (the logo shrinks),
     so anything that pins below it reads the real measurement rather than a
     hardcoded offset that would be wrong at some widths. */
  function measureTopbar() {
    var topbar = document.getElementById('topbar');
    var h = topbar ? Math.round(topbar.getBoundingClientRect().height) : 0;
    document.documentElement.style.setProperty('--topbar-h', h + 'px');
  }

  var wasStuck = null;

  function onScroll() {
    var topbar = document.getElementById('topbar');
    if (!topbar) return;
    var stuck = window.scrollY > 60;
    topbar.classList.toggle('is-stuck', stuck);
    /* the height only changes when it crosses that threshold, so measuring on
       every scroll frame would be a layout read for nothing */
    if (stuck !== wasStuck) {
      wasStuck = stuck;
      requestAnimationFrame(measureTopbar);
    }
  }

  /* ======================================================================
     TABS — scoped per section so two tab groups cannot cross-wire
     ====================================================================== */
  function initTabs(root) {
    var groups = root.querySelectorAll('[data-tabs]');
    Array.prototype.forEach.call(groups, function (group) {
      if (group.dataset.tabsReady === '1') return;
      group.dataset.tabsReady = '1';

      var btns = group.querySelectorAll('[data-tab]');
      var panels = group.querySelectorAll('[data-panel]');
      if (!btns.length) return;

      Array.prototype.forEach.call(btns, function (btn) {
        btn.addEventListener('click', function () {
          var i = btn.getAttribute('data-tab');
          Array.prototype.forEach.call(btns, function (b) {
            var on = b === btn;
            b.classList.toggle('is-on', on);
            b.setAttribute('aria-selected', on ? 'true' : 'false');
          });
          Array.prototype.forEach.call(panels, function (p) {
            p.classList.toggle('is-on', p.getAttribute('data-panel') === i);
          });
        });
      });
    });
  }

  /* ======================================================================
     YOUR PRIVACY CHOICES

     Shopify's consent panel has no stable URL, so the footer link calls the
     customer privacy API instead. If that API is absent — consent not enabled
     for the visitor's region, or the banner script blocked — the click is left
     alone and the href carries the visitor to the Privacy Policy.
     ====================================================================== */
  function initPrivacyChoices(root) {
    var links = root.querySelectorAll('[data-privacy-choices]');
    Array.prototype.forEach.call(links, function (link) {
      if (link.dataset.privacyReady === '1') return;
      link.dataset.privacyReady = '1';

      link.addEventListener('click', function (e) {
        var api = window.Shopify && window.Shopify.customerPrivacy;
        if (api && typeof api.showPreferences === 'function') {
          e.preventDefault();
          api.showPreferences();
        }
        /* otherwise the link follows its href */
      });
    });
  }

  /* ======================================================================
     BOOT
     ====================================================================== */
  function boot(root) {
    root = root || document;
    initYear(root);
    stampDelays(root);
    initReveals(root);
    initMarquee(root);
    initVideo(root);
    initTabs(root);
    initPinstage(root);
    initPrivacyChoices(root);
  }

  function start() {
    boot(document);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', measureTopbar, { passive: true });
    window.addEventListener('load', measureTopbar);

    /* sticking animates the padding and logo height over .4s, so the value
       read on the frame of the class change is the old one */
    var topbar = document.getElementById('topbar');
    if (topbar) topbar.addEventListener('transitionend', measureTopbar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Theme editor: a section is replaced wholesale on every settings change,
     so re-run against just that subtree. */
  document.addEventListener('shopify:section:load', function (e) {
    boot(e.target);
    onScroll();
  });

  /* Reveals are one-shot. When a merchant selects a section in the editor,
     show its content immediately rather than waiting for a scroll that the
     editor viewport may never produce. */
  document.addEventListener('shopify:section:select', function (e) {
    var hidden = e.target.querySelectorAll('[data-r]:not(.is-in), [data-mask]:not(.is-in), [data-wipe]:not(.is-in), [data-stagger]:not(.is-in)');
    Array.prototype.forEach.call(hidden, function (el) { el.classList.add('is-in'); });
  });
})();
